import { update } from "@intercom/messenger-js-sdk";

/**
 * Intercom's own default padding — how far the launcher floats off an edge.
 *
 * Also where the launcher rests: the viewport's own bottom-right corner, on
 * every page. It only leaves it for as long as something is in the way there.
 */
const BASE_PADDING_PX = 20;
/**
 * Width of Intercom's launcher — the pre-load placeholder and the real one are
 * the same 48px circle.
 *
 * Needed because the launcher is *lined up* with what it perches on rather than
 * merely kept clear of it, and the only position Intercom takes is an inset of
 * the launcher's edge, never of its centre.
 */
const LAUNCHER_SIZE_PX = 48;
/** Width of the company preview side sheet (`company-preview-panel-sheet.tsx`). */
const PREVIEW_PANEL_WIDTH_PX = 480;
/** Breathing room between the launcher and whatever it is riding above. */
const OBSTRUCTION_GAP_PX = 12;
/**
 * Step the clearance over an obstruction is rounded up to — a surface that
 * re-measures continuously (a panel being dragged) would otherwise fire a burst
 * of near-identical `update()` calls, each one a ping to Intercom's servers.
 *
 * Applied to that clearance alone, not to the placement as a whole: rounding a
 * resting position too would move the launcher off the very padding it is meant
 * to sit at, for no benefit.
 */
const VERTICAL_STEP_PX = 8;
/**
 * How long the launcher takes to slide when its new corner arrives in one step
 * — the company preview sheet's own open and close durations
 * (`company-preview-panel-sheet.tsx`), so the bubble travels *with* the sheet
 * pushing it aside rather than teleporting ahead of it.
 */
const PREVIEW_SHEET_OPEN_MS = 400;
const PREVIEW_SHEET_CLOSE_MS = 250;
/**
 * Same, for every other stepped move: the app's own panel slide
 * (`flex-grow 0.3s ease-in-out`, `useCompanyPageState.ts`), which is the pace
 * everything that opens or closes beside the launcher travels at.
 */
const PANEL_SLIDE_MS = 300;

/**
 * How far up from the bottom edge of the viewport an element reaches — its
 * footprint as the launcher sees it, which is not the same as its height.
 *
 * A surface that floats clear of the bottom edge still owns everything below it
 * as far as the launcher is concerned: only the distance to the bottom edge
 * says where the launcher has to stop.
 *
 * Deliberately blind to `visibility`. Nothing re-measures on a visibility flip
 * — a `ResizeObserver` sees size changes, and hiding an element leaves its size
 * untouched — so treating a hidden element as absent would strand the launcher
 * at whatever it read while the element was out of sight. A surface hidden for
 * a beat behind a loading skeleton keeps its place in the layout and is about
 * to be revealed there, so it is counted right away. One that is genuinely gone
 * is caught geometrically instead: a collapsed panel leaves its content
 * zero-sized or off screen, and that does re-measure.
 *
 * Blind to a hover effect too, and that one is not a detail: the company page's
 * chat toggle grows 5% under the pointer (`hover:scale-105`), which is 2.8px of
 * paint — and since the pointer is by definition resting on that button at the
 * moment it is clicked, the measure that follows the click is the puffed one.
 * 1.4px at the top is all it takes to cross a rounding step and lift the bubble
 * a whole 8px, for as long as the chat stays open. What the launcher has to
 * clear is the box the element *occupies*, not the one it paints while being
 * pointed at.
 */
export function bottomEdgeReach(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  if (!isOnScreen(rect)) return 0;
  // `offsetHeight` is the layout height, blind to transforms — and a scale
  // about the centre, which is the only kind these buttons use, overhangs the
  // layout box evenly above and below.
  const overhang =
    element.offsetHeight > 0
      ? Math.max(0, (rect.height - element.offsetHeight) / 2)
      : 0;
  // Clamped to the viewport: a surface taller than the screen would otherwise
  // push the launcher out through the top edge.
  return Math.min(
    window.innerHeight,
    Math.max(0, window.innerHeight - rect.top - overhang),
  );
}

/**
 * How far an element's right edge sits from the viewport's own — `null` when the
 * element isn't on screen, a panel collapsed to nothing above all.
 *
 * Read on the container a floating button is laid out from rather than on the
 * button itself, when that button is sliding: a transition reports the frame it
 * is on, not where it is headed, so a button measured the moment it is told to
 * move still answers with the place it is leaving.
 */
export function rightEdgeInset(element: HTMLElement): number | null {
  const rect = element.getBoundingClientRect();
  if (!isOnScreen(rect)) return null;
  return Math.max(0, window.innerWidth - rect.right);
}

function isOnScreen(rect: DOMRect): boolean {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.bottom > 0
  );
}

/**
 * A surface the launcher has to sit above, as the launcher sees it.
 */
export type SupportLauncherObstruction = {
  /** How far up from the viewport's bottom edge the surface reaches. */
  bottom: number;
  /**
   * Inset of the surface's centre from the viewport's right edge, when the
   * launcher is to *perch* on it — lined up with it, and not merely clear of
   * it. `null` leaves the launcher resting where it horizontally belongs.
   */
  centreRight: number | null;
};

/** Nothing in the corner: the launcher rests where it belongs. */
export const NO_OBSTRUCTION: SupportLauncherObstruction = {
  bottom: 0,
  centreRight: null,
};

export function isSameObstruction(
  a: SupportLauncherObstruction,
  b: SupportLauncherObstruction,
): boolean {
  return a.bottom === b.bottom && a.centreRight === b.centreRight;
}

/** What currently shares the corner the launcher floats in. */
export type SupportLauncherSurroundings = {
  /**
   * Whether the app is in its desktop format. The bubble is a desktop entry
   * point only: on a phone the bottom-right corner belongs to the tab bar, the
   * chat composer and each page's own action button, and support lives in the
   * account menu as it always has.
   */
  isDesktop: boolean;
  /** The tallest floating surface the launcher has to sit above. */
  obstruction: SupportLauncherObstruction;
  /** Whether the viewport-anchored company preview sheet is on screen. */
  hasPreviewSheet: boolean;
  /**
   * Whether a modal overlay currently holds the page — any dialog, alert or
   * modal sheet, the preview one included (see `modalOverlayStore`).
   */
  hasModalOverlay: boolean;
};

/** Where the launcher sits, and whether it shows at all. */
export type SupportLauncherPlacement = {
  /**
   * Hiding must stay a hiding — `hide_default_launcher`, never a padding that
   * parks the launcher off screen. The two travel through different
   * mechanisms: Intercom's pre-load launcher honours the flag (it is removed
   * from the DOM) and ignores the paddings entirely, so a bubble "hidden" by
   * being moved away would still be sitting in the corner until the messenger
   * finishes loading — and for good wherever it never does. See
   * {@link publishSupportLauncherCssPlacement}.
   */
  isHidden: boolean;
  verticalPadding: number;
  horizontalPadding: number;
};

/** The placement fields as Intercom itself names them. */
type IntercomLauncherSettings = {
  hide_default_launcher: boolean;
  vertical_padding: number;
  horizontal_padding: number;
};

/** Rounds a clearance up to the step it is allowed to vary by. */
function quantize(clearance: number): number {
  return Math.ceil(clearance / VERTICAL_STEP_PX) * VERTICAL_STEP_PX;
}

/**
 * Keeps the launcher out of the way of everything else the app floats over the
 * viewport edges.
 *
 * The launcher floats above the entire app (`z-index: 2147483000`), so it is
 * moved rather than layered.
 *
 * It rests in the viewport's own bottom-right corner, at Intercom's default
 * padding — the corner a user reaches for without looking, and the one the
 * bubble goes back to as soon as nothing is in it.
 *
 * From there it steps aside for whatever shares that corner: left of the
 * company preview sheet, and up over a registered obstruction — today, the
 * company page's chat toggle while the chat is docked beside it. That one asks
 * for more than clearance: the launcher perches on the button, centred over it,
 * because the pair reads as one control column at the seam between the company
 * and its chat rather than as a bubble that happens to float nearby.
 *
 * And it hides outright while a modal overlay holds the page: dimmed or not,
 * the layer below is not meant to be reachable, and a launcher floating above
 * the overlay would be the one thing on screen that still is.
 */
export function supportLauncherPlacement({
  isDesktop,
  obstruction,
  hasPreviewSheet,
  hasModalOverlay,
}: SupportLauncherSurroundings): SupportLauncherPlacement {
  if (!isDesktop) {
    return {
      isHidden: true,
      verticalPadding: BASE_PADDING_PX,
      horizontalPadding: BASE_PADDING_PX,
    };
  }

  const clearsPreviewSheet = hasPreviewSheet && !hasModalOverlay;
  const { bottom, centreRight } = obstruction;
  return {
    isHidden: hasModalOverlay,
    verticalPadding: Math.max(
      BASE_PADDING_PX,
      quantize(bottom + OBSTRUCTION_GAP_PX),
    ),
    // A perch places the launcher's centre, so half of it is given back; the
    // resting padding is still the floor, and the preview sheet still wins over
    // a perch it would be covering.
    horizontalPadding: Math.round(
      Math.max(
        BASE_PADDING_PX + (clearsPreviewSheet ? PREVIEW_PANEL_WIDTH_PX : 0),
        centreRight === null ? 0 : centreRight - LAUNCHER_SIZE_PX / 2,
      ),
    ),
  };
}

export function isSamePlacement(
  applied: SupportLauncherPlacement | null,
  next: SupportLauncherPlacement,
): boolean {
  return (
    applied !== null &&
    applied.isHidden === next.isHidden &&
    applied.verticalPadding === next.verticalPadding &&
    applied.horizontalPadding === next.horizontalPadding
  );
}

/**
 * How long each leg of a move takes — `0` when the launcher must simply *be*
 * there, without any easing of its own.
 *
 * The two legs are timed apart because they are not the same kind of move. The
 * rise always arrives in one step: a surface to clear is under the launcher or
 * it is not, and nothing measures a height continuously. The slide can be
 * either — a sheet that is suddenly there is a step, while a perch is measured
 * on every frame of the slide that carries it.
 *
 * Keeping them apart is what lets the two happen at once: while the launcher
 * eases up onto a chat toggle, it also rides that toggle's column frame by
 * frame, and neither move cuts the other short (see
 * {@link publishSupportLauncherCssPlacement}).
 */
export type SupportLauncherMove = {
  /** Milliseconds the vertical leg eases over. */
  rise: number;
  /** Milliseconds the horizontal leg eases over. */
  slide: number;
};

/**
 * How long the launcher should take to travel from one placement to the next.
 *
 * The launcher is never the thing moving first: it steps aside for a panel or a
 * sheet that is itself sliding, so it travels at that surface's pace.
 *
 * The first placement of all is not a move: there is nowhere the bubble is
 * coming from, so it is published where it belongs at once.
 */
export function supportLauncherMove(
  previous: SupportLauncherSurroundings | null,
  next: SupportLauncherSurroundings,
): SupportLauncherMove {
  if (previous === null) return { rise: 0, slide: 0 };
  return { rise: PANEL_SLIDE_MS, slide: slideDuration(previous, next) };
}

function slideDuration(
  previous: SupportLauncherSurroundings,
  next: SupportLauncherSurroundings,
): number {
  if (previous.hasPreviewSheet !== next.hasPreviewSheet) {
    return next.hasPreviewSheet ? PREVIEW_SHEET_OPEN_MS : PREVIEW_SHEET_CLOSE_MS;
  }
  // A perch the page is measuring on every frame of a slide already carries
  // that slide's easing; easing it a second time would leave the launcher
  // trailing behind the very button it is riding. Taking one up, or putting one
  // down, is a step like any other.
  if (previous.obstruction.centreRight !== null && next.obstruction.centreRight !== null) {
    return 0;
  }
  return PANEL_SLIDE_MS;
}

function toIntercomSettings({
  isHidden,
  verticalPadding,
  horizontalPadding,
}: SupportLauncherPlacement): IntercomLauncherSettings {
  return {
    hide_default_launcher: isHidden,
    vertical_padding: verticalPadding,
    horizontal_padding: horizontalPadding,
  };
}

/**
 * Placement the messenger boots with: nothing is on screen yet beyond the
 * shell itself.
 *
 * `isDesktop` is what the caller knows at boot time, which on the very first
 * render is "not yet" — and that has to boot the launcher hidden rather than
 * visible, since a bubble flashed into a phone's corner cannot be taken back
 * while a hidden one is simply shown a tick later, by the driver.
 *
 * Also what the placement driver takes as already applied after a boot — every
 * boot resets the launcher to this, its own included.
 */
export function initialSupportLauncherPlacement(
  isDesktop: boolean,
): SupportLauncherPlacement {
  return supportLauncherPlacement({
    isDesktop,
    obstruction: NO_OBSTRUCTION,
    hasPreviewSheet: false,
    hasModalOverlay: false,
  });
}

/** {@link initialSupportLauncherPlacement}, as Intercom's boot takes it. */
export function initialSupportLauncherSettings(
  isDesktop: boolean,
): IntercomLauncherSettings {
  return toIntercomSettings(initialSupportLauncherPlacement(isDesktop));
}

/**
 * State of the move in flight — module-level because a move outlives the render
 * that started it, and the next publication has to reckon with it.
 *
 * `legEndsAt` holds when each leg is due to stop easing (`0` for a leg that
 * isn't), so that publishing one leg leaves the other's timing untouched.
 */
let publishedPlacement: SupportLauncherPlacement | null = null;
const legEndsAt: Record<"rise" | "slide", number> = { rise: 0, slide: 0 };
let easedMoveTimeout: ReturnType<typeof setTimeout> | null = null;

/** Gives one leg its own easing — or takes it away, for a leg that must land. */
function easeLeg(leg: "rise" | "slide", durationMs: number): void {
  document.documentElement.style.setProperty(
    `--support-launcher-${leg}-duration`,
    `${durationMs}ms`,
  );
  legEndsAt[leg] = durationMs > 0 ? Date.now() + durationMs : 0;
}

/**
 * Marks the launcher as travelling for as long as either leg still is, and
 * hands it back its own transitions once neither is.
 */
function refreshEasedMoveMark(): void {
  if (easedMoveTimeout !== null) {
    clearTimeout(easedMoveTimeout);
    easedMoveTimeout = null;
  }
  const root = document.documentElement;
  const remaining = Math.max(legEndsAt.rise, legEndsAt.slide) - Date.now();
  if (remaining <= 0) {
    legEndsAt.rise = 0;
    legEndsAt.slide = 0;
    delete root.dataset.supportLauncherMove;
    return;
  }
  root.dataset.supportLauncherMove = "eased";
  easedMoveTimeout = setTimeout(() => {
    easedMoveTimeout = null;
    legEndsAt.rise = 0;
    legEndsAt.slide = 0;
    delete root.dataset.supportLauncherMove;
  }, remaining);
}

/**
 * Mirrors the placement into CSS custom properties — where both of Intercom's
 * launchers actually sit, and how long they take to get there.
 *
 * Position, because neither launcher can be moved through the settings the way
 * this app needs. The pre-load bubble Intercom paints before its own app has
 * loaded is positioned by Intercom's stylesheet alone (`bottom: 20px; right:
 * 20px`) and is blind to the paddings, so `update()` never moves it at all. The
 * real one does honour them, but only through a call that has to be debounced —
 * it pings Intercom's servers — so it would arrive a beat *after* the panel
 * that pushed it, as a jump. `index.css` restates both positions from these
 * properties instead, and the paddings stay what tells Intercom itself where
 * the launcher is, since that is where it unfolds the messenger.
 *
 * Duration, because a move that arrives in one step has to be eased into (see
 * {@link supportLauncherMove}), and CSS cannot tell the two kinds of move
 * apart. It is published as an attribute rather than a `0ms` duration so that
 * the transition rule only exists while the launcher is travelling: Intercom
 * gives its own launcher a `transform` transition, and a standing `transition`
 * declaration of ours would override it for good.
 *
 * Each leg is timed only when it is the one moving. A launcher easing up onto a
 * chat toggle while riding that toggle's column reads a new horizontal position
 * every frame, and re-timing the rise on each of them would cancel it and drop
 * the launcher at its destination in one jump.
 *
 * Published on every placement rather than only the ones that reach Intercom:
 * a placement equal to the one the boot carried sends no `update()` at all,
 * and the launchers still have to be told where to sit.
 */
export function publishSupportLauncherCssPlacement(
  placement: SupportLauncherPlacement,
  { rise, slide }: SupportLauncherMove,
): void {
  const root = document.documentElement;
  const { verticalPadding, horizontalPadding } = placement;
  if (publishedPlacement?.verticalPadding !== verticalPadding) {
    easeLeg("rise", rise);
  }
  if (publishedPlacement?.horizontalPadding !== horizontalPadding) {
    easeLeg("slide", slide);
  }
  publishedPlacement = placement;
  root.style.setProperty("--support-launcher-bottom", `${verticalPadding}px`);
  root.style.setProperty("--support-launcher-right", `${horizontalPadding}px`);
  refreshEasedMoveMark();
}

/**
 * Moves the launcher, and makes the move stick.
 *
 * `update()` alone does not hold. Intercom re-derives the launcher's position
 * from `window.intercomSettings` on *every* refresh of the messenger, including
 * the ones it fires itself (page-change pings on an SPA, boot retries) — so a
 * padding handed to `update()` only survives until the next of those, then
 * silently snaps back to whatever the messenger booted with. Writing the
 * placement into the settings object first makes it the value those refreshes
 * re-derive from; the `update()` call is what applies it right away.
 */
export function applySupportLauncherPlacement(
  placement: SupportLauncherPlacement,
): void {
  const settings = toIntercomSettings(placement);
  if (window.intercomSettings) {
    Object.assign(window.intercomSettings, settings);
  }
  update(settings);
}
